import React, { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { IoMdLock } from "react-icons/io";
import { CiCircleQuestion } from "react-icons/ci";
import { FiPlus, FiTrash2 } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import "./biographyMembers.css";
import { REST_API_URL } from "../../api/config";

const BASE_URL = REST_API_URL;

const getUserTimeZone = () => {
  if (typeof window === "undefined") return "UTC";
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
};

const buildHeaders = (token, extra = {}) => ({
  "Content-Type": "application/json",
  ...(token ? { Authorization: `Bearer ${token}` } : {}),
  "User-Timezone": getUserTimeZone(),
  ...extra,
});

export default function BiographyMember() {
  const [email, setEmail] = useState("");
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    localStorage.setItem("onboarding_code", "226");
    sessionStorage.setItem("onboarding_force_route", "/biography-member");
    sessionStorage.setItem("onboarding_force_status", "salon");
  }, []);

  const token = useMemo(() => {
    return (
      localStorage.getItem("accessToken") ||
      sessionStorage.getItem("accessToken") ||
      ""
    ).trim();
  }, []);

  const handleAddMember = () => {
    if (!email.trim()) {
      toast.error("Please enter an email.");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Invalid email address.");
      return;
    }

    if (members.includes(email)) {
      toast.error("This member is already added.");
      return;
    }

    setMembers((prev) => [...prev, email]);
    setEmail("");
    toast.success("Member added!");
  };

  const handleRemoveMember = (member) => {
    setMembers((prev) => prev.filter((m) => m !== member));
  };

  const handleNextOrSkip = async () => {
    if (members.length === 0) {
      localStorage.setItem("onboarding_code", "228");
      sessionStorage.setItem("onboarding_force_route", "/choose-niche");
      sessionStorage.setItem("onboarding_force_status", "salon");
      navigate("/choose-niche", { replace: true });
      return;
    }

    setLoading(true);

    const salonData = JSON.parse(localStorage.getItem("salonData") || "{}");

    try {
      if (!salonData.full_name || !salonData.company_name) {
        toast.error("Missing salon info. Please go back and fill in step 1.");
        navigate(-1);
        return;
      }

      if (!token) {
        toast.error("No access token found. Please log in again.");
        navigate("/sign-in");
        return;
      }

      const userTimeZone = getUserTimeZone();

      const body = {
        ...salonData,
        worker_emails: members,
        timeZone: userTimeZone,
      };

      const userInfoUrl = `${BASE_URL}/auth/userinfo/`;

      const res = await fetch(userInfoUrl, {
        method: "PUT",
        headers: buildHeaders(token),
        body: JSON.stringify(body),
      });

      if (res.status === 401) {
        toast.error("Session expired. Please log in again.");
        navigate("/sign-in");
        return;
      }

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        console.error("API error:", data);
        throw new Error(data.detail || "Failed to update members.");
      }

      localStorage.setItem("salonData", JSON.stringify(body));
      localStorage.setItem("user_timezone", userTimeZone);
      localStorage.setItem("onboarding_code", "228");

      sessionStorage.setItem("onboarding_force_route", "/choose-niche");
      sessionStorage.setItem("onboarding_force_status", "salon");

      toast.success("Members added successfully!");
      navigate("/choose-niche", { replace: true });
    } catch (err) {
      console.error("Error:", err);
      toast.error(err.message || "Failed to send members.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleEnterKey = (e) => {
      if (e.key === "Enter" && email.trim() && !loading) {
        e.preventDefault();
        handleAddMember();
      }
    };

    window.addEventListener("keydown", handleEnterKey);
    return () => window.removeEventListener("keydown", handleEnterKey);
  }, [email, loading]);

  useEffect(() => {
    const emailInput = document.querySelector(".biography-member-add-form input");
    if (emailInput) {
      emailInput.focus();
    }
  }, []);

  return (
    <motion.div
      className="biography-member"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <main className="biography-member-card">
        <div className="biography-member-header">
          <div className="biography-member-text">
            <h2>Biography</h2>
            <p>Tell us about your business</p>
          </div>
        </div>

        <div className="biography-member-content">
          <label className="biography-member-label">Add your member's email</label>
          <p className="biography-member-subtitle">
            Your staff will receive an email invitation to complete their registration
          </p>

          <div className="biography-member-add-form">
            <input
              type="email"
              placeholder="Type your member email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && email.trim()) {
                  e.preventDefault();
                  handleAddMember();
                }
              }}
            />
            <button
              className="biography-member-add-btn"
              onClick={handleAddMember}
              disabled={!email.trim() || loading}
            >
              Add Member <FiPlus size={16} />
            </button>
          </div>

          <div className="biography-member-list">
            {members.length === 0 ? (
              <div className="biography-member-empty-state">
                <p>No members added yet</p>
                <small>Add email addresses above to invite team members</small>
              </div>
            ) : (
              members.map((member, i) => (
                <div key={i} className="biography-member-item">
                  <span className="biography-member-email">{member}</span>
                  <button
                    className="biography-member-delete"
                    onClick={() => handleRemoveMember(member)}
                    aria-label={`Remove ${member}`}
                  >
                    <FiTrash2 size={16} />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="biography-member-footer-buttons">
          <motion.button
            className="biography-member-skip"
            onClick={handleNextOrSkip}
            disabled={loading}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {loading ? "Saving..." : members.length > 0 ? "Next" : "Skip"}
          </motion.button>
          <span className="biography-member-step">Step 2/5</span>
        </div>
      </main>

      <footer className="biography-member-footer">
        <span>© 2025 Halper. All rights reserved.</span>
        <div className="biography-member-footer-links">
          <a href="#">
            <IoMdLock /> Privacy
          </a>
          <a href="#">
            <CiCircleQuestion /> Get help
          </a>
        </div>
      </footer>
    </motion.div>
  );
}